import { validateNodeIdFormat } from './graphMutations';

function clone(obj) { return JSON.parse(JSON.stringify(obj)); }

// Canonical fixer order. Each entry produces a plain-english label for the
// auto-fix picker; the label receives the change count.
const FIXERS = [
  { key: 'E001', fn: autoFixE001, label: n => `Remove ${n} connector${n === 1 ? '' : 's'} pointing at deleted nodes` },
  { key: 'E002', fn: autoFixE002, label: n => `Remove ${n} duplicate connector${n === 1 ? '' : 's'}` },
  { key: 'E018', fn: autoFixE018, label: n => `Reconnect ${n} connector${n === 1 ? '' : 's'} written as "End" to the actual End node` },
  { key: 'E003', fn: autoFixE003, label: n => `Give distinct priorities to ${n} unconditional connector${n === 1 ? '' : 's'} sharing a source` },
  { key: 'E010', fn: autoFixE010, label: n => `Rename ${n} node${n === 1 ? '' : 's'} with duplicate IDs` },
  { key: 'E016', fn: autoFixE016, label: n => `Fill in ${n} missing or invalid variable name${n === 1 ? '' : 's'}` },
  { key: 'E017', fn: autoFixE017, label: n => `Rewrite ${n} condition${n === 1 ? '' : 's'} to use variable names instead of question IDs` },
];

function runFixers({ nodes, edges }, keys = null) {
  let n = clone(nodes);
  let e = clone(edges);
  let changes = 0;
  const ops = [];
  const items = [];
  for (const fixer of FIXERS) {
    if (keys && !keys.includes(fixer.key)) continue;
    const res = fixer.fn({ nodes: n, edges: e, changes: 0, ops: [] });
    n = res.nodes;
    e = res.edges;
    if (res.changes > 0) {
      changes += res.changes;
      ops.push(...res.ops);
      items.push({
        key: fixer.key,
        count: res.changes,
        label: fixer.label(res.changes),
        detail: res.ops.join('; '),
      });
    }
  }
  return { nodes: n, edges: e, changes, ops, items };
}

export function applyAutoFixes({ nodes = [], edges = [] }) {
  const res = runFixers({ nodes, edges });
  if (res.changes === 0) return { nodes, edges, changes: 0, ops: [] };
  return res;
}

// Apply only the fixers the author selected in the auto-fix picker.
export function applySelectedAutoFixes({ nodes = [], edges = [] }, keys = []) {
  const res = runFixers({ nodes, edges }, keys);
  if (res.changes === 0) return { nodes, edges, changes: 0, ops: [] };
  return res;
}

export function planAutoFixes({ nodes = [], edges = [] }) {
  // Dry-run: calls fixers on clones to produce ops list and a proposal
  return applyAutoFixes({ nodes: clone(nodes), edges: clone(edges) });
}

// Dry-run that returns one entry per fixer with a count and a plain-english
// label, for the auto-fix picker modal.
export function planAutoFixesDetailed({ nodes = [], edges = [] }) {
  const res = runFixers({ nodes: clone(nodes), edges: clone(edges) });
  return { items: res.items, totalChanges: res.changes };
}

export function autoFixE001({ nodes, edges, changes = 0, ops = [] }) {
  const idSet = new Set(nodes.map(n => String(n.id)));
  const filtered = edges.filter(ed => idSet.has(String(ed.source)) && idSet.has(String(ed.target)));
  if (filtered.length !== edges.length) {
    const removed = (edges.length - filtered.length);
    changes += removed;
    ops.push(`E001: removed ${removed} edge(s) referencing missing nodes`);
  }
  return { nodes, edges: filtered, changes, ops };
}

export function autoFixE002({ nodes, edges, changes = 0, ops = [] }) {
  const seen = new Set();
  const out = [];
  let removed = 0;
  for (const ed of edges) {
    const cond = (ed.data && String(ed.data.condition || '').trim()) || '';
    const key = `${ed.source}→${ed.target}#${cond}`;
    if (seen.has(key)) { changes++; removed++; continue; }
    seen.add(key);
    out.push(ed);
  }
  if (removed) ops.push(`E002: deduped ${removed} duplicate edge(s)`);
  return { nodes, edges: out, changes, ops };
}

export function autoFixE003({ nodes, edges, changes = 0, ops = [] }) {
  const bySrc = new Map();
  for (const ed of edges) {
    const cond = (ed.data && String(ed.data.condition || '').trim()) || '';
    if (!bySrc.has(ed.source)) bySrc.set(ed.source, []);
    bySrc.get(ed.source).push({ ed, cond });
  }
  let adjustedGroups = 0;
  for (const [src, arr] of bySrc.entries()) {
    const uncond = arr.filter(x => !x.cond).map(x => x.ed);
    if (uncond.length > 1) {
      // Sort by current numeric priority desc, invalid as 0
      uncond.sort((a, b) => ((Number(b.data?.priority) || 0) - (Number(a.data?.priority) || 0)));
      // Assign distinct priorities high→low
      let pri = uncond.length;
      for (const ed of uncond) {
        const newPri = pri--;
        if (!ed.data) ed.data = {};
        if (ed.data.priority !== newPri) { ed.data.priority = newPri; changes++; }
      }
      adjustedGroups++;
    }
  }
  if (adjustedGroups) ops.push(`E003: assigned distinct priorities for ${adjustedGroups} source group(s)`);
  return { nodes, edges, changes, ops };
}

export function autoFixE010({ nodes, edges, changes = 0, ops = [] }) {
  // Rename the second and later occurrences of a duplicated node id.
  // The first occurrence keeps the id, so existing edges keep pointing at it
  // (edge references to a duplicated id are ambiguous; first-wins resolves it).
  const seen = new Set();
  const taken = new Set(nodes.map(n => String(n.id)));
  let renamed = 0;
  const out = nodes.map(n => {
    const id = String(n.id);
    if (!seen.has(id)) { seen.add(id); return n; }
    let idx = 2;
    let newId;
    do { newId = `${id}_${idx++}`; } while (taken.has(newId) || !validateNodeIdFormat(newId).ok);
    taken.add(newId);
    renamed++;
    const data = { ...(n.data || {}) };
    if (String(data.id || '') === id) data.id = newId;
    return { ...n, id: newId, data };
  });
  if (renamed === 0) return { nodes, edges, changes, ops };
  ops.push(`E010: renamed ${renamed} node(s) with duplicate ids`);
  return { nodes: out, edges, changes: changes + renamed, ops };
}

export function autoFixE016({ nodes, edges, changes = 0, ops = [] }) {
  const varSet = new Set();
  for (const n of nodes) {
    if (n.type === 'question') {
      const v = (n.data && (n.data.variable || n.data.variable_name)) || '';
      const vTrim = String(v).trim();
      if (vTrim) varSet.add(vTrim.toLowerCase());
    }
  }
  function isValidVarName(v) { return /^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(v); }
  let fixed = 0;
  for (const n of nodes) {
    if (n.type !== 'question') continue;
    const collects = (n.collectsInput !== undefined) ? (n.collectsInput !== false) : (n.data?.collectsInput !== false);
    if (!collects) continue;
    const current = (n.data && (n.data.variable || n.data.variable_name)) || '';
    let v = String(current).trim();
    if (!isValidVarName(v)) {
      // Derive from text or id
      const base = (String(n.data?.text || '').trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_') || `var_${n.id}`).replace(/^_+|_+$/g,'');
      let candidate = base || `var_${n.id}`;
      if (!/^[A-Za-z_]/.test(candidate)) candidate = `v_${candidate}`;
      let suffix = 1;
      while (varSet.has(candidate.toLowerCase())) candidate = `${base}_${suffix++}`;
      if (!n.data) n.data = {};
      n.data.variable = candidate;
      varSet.add(candidate.toLowerCase());
      changes++;
      fixed++;
    }
  }
  if (fixed) ops.push(`E016: filled in ${fixed} missing/invalid variable name(s)`);
  return { nodes, edges, changes, ops };
}

export function autoFixE017({ nodes, edges, changes = 0, ops = [] }) {
  // Build map from question_id → variable_name when valid
  const idToVar = new Map();
  for (const n of nodes) {
    if (n.type === 'question') {
      const v = (n.data && (n.data.variable || n.data.variable_name)) || '';
      if (/^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(v)) idToVar.set(String(n.id), v);
    }
  }
  if (idToVar.size === 0) return { nodes, edges, changes, ops };
  const wordBoundary = (s) => new RegExp(`\\b${s}\\b`, 'g');
  for (const ed of edges) {
    const cond = (ed.data && ed.data.condition) || '';
    if (!cond || typeof cond !== 'string') continue;
    let updated = cond;
    for (const [qid, vname] of idToVar.entries()) {
      updated = updated.replace(wordBoundary(qid), vname);
    }
    if (updated !== cond) {
      if (!ed.data) ed.data = {};
      ed.data.condition = updated;
      changes++;
    }
  }
  if (changes) ops.push('E017: rewrote condition identifiers from question_id to variable_name');
  return { nodes, edges, changes, ops };
}

export function autoFixE018({ nodes, edges, changes = 0, ops = [] }) {
  const endNodes = nodes.filter(n => n.type === 'end');
  const endId = endNodes.length === 1 ? String(endNodes[0].id) : null;
  if (!endId) return { nodes, edges, changes, ops };
  let changed = 0;
  for (const ed of edges) {
    const t = String(ed.target || '').trim();
    if (/^(end|End)$/i.test(t)) {
      ed.target = endId;
      changed++;
    }
  }
  if (changed) ops.push(`E018: normalized ${changed} End token target(s) to '${endId}'`);
  return { nodes, edges, changes: changes + changed, ops };
}


